/**
 * ProductsContext
 *
 * This context provides product information via REST API.
 * It works independently of WebSocket connections and does not require authentication.
 * Products are fetched from the public API endpoint: /products/public
 *
 * This ensures that product information is available on the landing page
 * and in all other contexts, regardless of authentication status or WebSocket connectivity.
 */
import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import api from '../../services/api';

export interface ProductPrice {
  price: number;
  currency_code: string;
  discounted_price: number | null;
}

export interface VolumeDiscountTier {
  price: number;
  min_qty: number;
  discounted_price: number;
}

export interface VolumeDiscount {
  tiers: VolumeDiscountTier[];
  currency_code: string;
}

export interface ProductSubscription {
  interval: string;
  intervalCount: number;
}

export interface ProductDetails {
  subscription?: ProductSubscription;
  checkout_cancel_url?: string;
  checkout_success_url?: string;
  [key: string]: any;
}

export interface AccountConfig {
  currency: string;
  profit_split: number;
  risk_max_loss: number;
  targets_profit: number;
  initial_balance: number;
  min_trading_days: number;
  contract_limits_minis: number;
  contract_limits_micros: number;
}

export interface Product {
  id: string;
  name: string;
  type: string;
  term_days: string | null;
  details: ProductDetails;
  prices: ProductPrice[];
  volume_discounts: VolumeDiscount[];
  discount_possible: boolean;
  extension_product_id: string | null;
  account_config?: AccountConfig;
}

interface ProductsContextType {
  products: Product[];
  isLoading: boolean;
  error: string | null;
  getProductById: (id: string) => Product | undefined;
  refreshProducts: () => Promise<void>;
}

const ProductsContext = createContext<ProductsContextType | undefined>(undefined);

export const ProductsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/products/public');

      if (response.data && response.data.products) {
        setProducts(response.data.products);
      } else {
        setError('Invalid response format');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch products');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const getProductById = (id: string): Product | undefined => {
    return products.find(product => product.id === id);
  };

  const refreshProducts = async () => {
    await fetchProducts();
  };

  return (
    <ProductsContext.Provider
      value={{
        products,
        isLoading,
        error,
        getProductById,
        refreshProducts,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = (): ProductsContextType => {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error('useProducts must be used within ProductsProvider');
  return ctx;
};
